import { IonText } from "@ionic/react";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import Provider from "@components/Provider";
import styles from "@components/Step.module.css";
import TextWithIcon from "@components/text/TextWithIcon";
import ExternalLink from "@components/typography/ExternalLink";
import {
  AssessmentStepSkipReasonEnum,
  AssessmentStepTypeEnum,
  GetJourneyWithOnboardingInfoQuery,
  ProviderFragment as ProviderType
} from "@typing/Generated";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type ProviderQuestionType = GetJourneyWithOnboardingInfoQuery["journey"]["assessments"][number]["steps"][number];

type Props = {
  backAllowed: boolean;
  handleBack: () => void;
  handleSkipReasonToggle: (reason: AssessmentStepSkipReasonEnum) => void;
  journeyId: string;
  nextAllowed: boolean;
  onCreate: (step: AssessmentStepTypeEnum) => void;
  onEdit: (provider: ProviderType) => void;
  onUpdate: () => void;
  providers?: ProviderType[];
  question: ProviderQuestionType;
  skipReason: AssessmentStepSkipReasonEnum | null;
};

const ProviderStep = ({
  backAllowed,
  handleBack,
  handleSkipReasonToggle,
  journeyId,
  nextAllowed,
  onCreate,
  onEdit,
  onUpdate,
  providers,
  question,
  skipReason
}: Props) => {
  const intl = useIntl();

  const handleCreate = useCallback(() => {
    onCreate(question.step);
  }, [onCreate, question.step]);

  const generateHandleSkipReason = useCallback(
    (reason: AssessmentStepSkipReasonEnum) => () => {
      handleSkipReasonToggle(reason);
    },
    [handleSkipReasonToggle]
  );

  return (
    <>
      <div className={styles.content}>
        <div className={styles.title}>
          <IonText>
            <FormattedMessage id={`pages.client.providers.${question.step}.title`} />
          </IonText>
        </div>
        <div className={styles.description}>
          <FormattedMessage id={`pages.client.providers.${question.step}.description`} />
          {!providers?.length && question.step === AssessmentStepTypeEnum.prescriptions && (
            <div className="ion-margin-top">
              <FormattedMessage
                id="pages.client.providers.blankForm"
                values={{
                  link: (
                    <ExternalLink href="">
                      <FormattedMessage id="pages.client.providers.blankLink" />
                    </ExternalLink>
                  )
                }}
              />
            </div>
          )}
        </div>

        <div className={styles.addedItem}>
          {providers?.map(provider => (
            <Provider journeyId={journeyId} key={provider.id} onEdit={onEdit} provider={provider} />
          ))}
        </div>

        <div className="ion-margin-bottom">
          <div
            className={styles.addButton}
            data-test="add-provider"
            onClick={handleCreate}
            onKeyUp={clickCurrentTarget}
            tabIndex={0}
          >
            <TextWithIcon
              icon="add"
              iconFirst
              text={intl.formatMessage({ id: `pages.client.providers.${question.step}.addProvider` })}
            />
          </div>
        </div>

        {!providers?.length ? (
          <div className={styles.checkbox}>
            <div className={styles.item}>
              <ToggleCheckbox
                checked={skipReason === AssessmentStepSkipReasonEnum.does_not_have}
                labelKey={`pages.client.providers.${question.step}.toggleOne`}
                name="noProvider"
                onClick={generateHandleSkipReason(AssessmentStepSkipReasonEnum.does_not_have)}
                tabIndex={0}
              />
            </div>
            <div className={styles.item}>
              <ToggleCheckbox
                checked={skipReason === AssessmentStepSkipReasonEnum.no_access}
                labelKey={`pages.client.providers.${question.step}.toggleTwo`}
                name="noAccess"
                onClick={generateHandleSkipReason(AssessmentStepSkipReasonEnum.no_access)}
                tabIndex={0}
              />
            </div>
          </div>
        ) : (
          <div className={styles.nextButton} />
        )}
      </div>
      <div className={styles.buttons}>
        <SecondaryButton
          data-test="backButton"
          disabled={!backAllowed}
          onClick={handleBack}
          textKey="models.assessments.goBack"
        />
        <PrimaryButton
          data-test="next-button"
          disabled={!nextAllowed}
          onClick={onUpdate}
          textKey={
            question.step === AssessmentStepTypeEnum.other_care_providers ? "dictionary.complete" : "dictionary.next"
          }
        />
      </div>
    </>
  );
};
export default ProviderStep;
